import { type Component, createSignal, Show } from 'solid-js'
import type { Subscriber } from '../../features/subscribers/domain/models/subscriber'
import { formatCountryName } from '../../shared/helpers/country-formatter'
import { formatDateToRegular } from '../../shared/helpers/date.functions'
import { useTransContext } from '@mbarzda/solid-i18next'

export interface UserDirectoryTableRowProps {
  readonly subscriber: Subscriber
  readonly isLoading?: boolean
}

const formatPlanLabel = (plan: string | null, t: (key: string) => string): string => {
  if (!plan) return ''
  if (plan.toLowerCase().includes('year')) return t('ls_generic_yearly')
  if (plan.toLowerCase().includes('month')) return t('ls_generic_monthly')
  return plan
}

const UserDirectoryTableRow: Component<UserDirectoryTableRowProps> = (props) => {
  const [t] = useTransContext()
  const [imgError, setImgError] = createSignal(false)

  const displayName = () => {
    if (props.subscriber.name?.trim()) return props.subscriber.name.trim()
    if (props.subscriber.username) return props.subscriber.username
    return props.subscriber.email
  }

  const initials = () => {
    const name = displayName()
    const parts = name.split(' ').filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return name.charAt(0).toUpperCase()
  }

  const displayRegion = () => {
    const parts: string[] = []
    if (props.subscriber.region) parts.push(props.subscriber.region)
    if (props.subscriber.country) parts.push(formatCountryName(props.subscriber.country))
    return parts.length > 0 ? parts.join(', ') : ''
  }

  return (
    <tr
      class="transition-opacity duration-300"
      classList={{
        'hover:bg-gray-50': !props.isLoading,
        'animate-pulse opacity-40 pointer-events-none': !!props.isLoading
      }}
    >
      <td class="max-w-0 px-4 py-3">
        <div class="flex min-w-0 items-center gap-2">
          <Show
            when={props.subscriber.image_url && !imgError()}
            fallback={
              <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700">
                {initials()}
              </div>
            }
          >
            <img
              src={props.subscriber.image_url!}
              alt={displayName()}
              class="h-8 w-8 flex-shrink-0 rounded-full object-cover"
              onError={() => setImgError(true)}
            />
          </Show>
          <span class="truncate text-sm font-medium text-gray-700" title={displayName()}>
            {displayName()}
          </span>
        </div>
      </td>
      <td class="max-w-0 px-4 py-3">
        <span class="block truncate text-sm text-gray-700" title={props.subscriber.email}>
          {props.subscriber.email}
        </span>
      </td>
      <td class="max-w-0 px-4 py-3">
        <span class="block truncate text-sm text-gray-700" title={props.subscriber.username ?? ''}>
          {props.subscriber.username}
        </span>
      </td>
      <td class="max-w-0 px-4 py-3">
        <span class="block truncate text-sm text-gray-700" title={displayRegion()}>
          {displayRegion()}
        </span>
      </td>
      <td class="max-w-0 px-4 py-3">
        <span class="block truncate text-sm text-gray-700" title={formatPlanLabel(props.subscriber.subscription_plan, t)}>
          {formatPlanLabel(props.subscriber.subscription_plan, t)}
        </span>
      </td>
      <td class="px-4 py-3">
        <span class="block truncate text-sm text-gray-700" title={props.subscriber.subscription_level ?? ''}>
          {props.subscriber.subscription_level}
        </span>
      </td>
      <td class="px-4 py-3">
        <span class="block truncate text-sm text-gray-700">
          {props.subscriber.joined_at ? formatDateToRegular(new Date(props.subscriber.joined_at)) : ''}
        </span>
      </td>
      <td class="px-4 py-3">
        <span class="block truncate text-sm text-gray-700" title={props.subscriber.subscription_status ?? ''}>
          {props.subscriber.subscription_status}
        </span>
      </td>
    </tr>
  )
}

export default UserDirectoryTableRow
